import { useState } from "react";

type Props = {
  formId: string;
  formName: string;
  onClose: () => void;
};

export default function GetCodeModal({ formId, formName, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<"link" | "iframe" | "embed" | "webhook">("link");
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/forms/${formId}`;

  const embedCode = `<script src="${window.location.origin}/forms/widget.js"></script>
<div id="crmpro-form"
  data-form-id="${formId}"
  data-url="${publicUrl}">
</div>`;

  const iframeCode = `<iframe
  src="${publicUrl}"
  width="100%"
  height="700"
  frameborder="0">
</iframe>`;

  const webhookUrl = `${window.location.origin}/api/v1/lead-capture/submit`;
  const jsonExample = `{
  "form_id": "${formId}",
  "data": {
    "Full Name": "Aasib Khan",
    "Phone Number": "9876543210",
    "Email Address": "aasib@example.com",
    "Message": "Interested in property inquiries"
  }
}`;

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">Share & Embed Form</h2>
            <p className="text-xs text-foreground-700 mt-0.5">{formName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {(["link", "iframe", "embed", "webhook"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeTab === tab
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "link" ? "Share Link" : tab === "iframe" ? "iFrame Embed" : tab === "embed" ? "JS Widget" : "API Endpoint"}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === "link" && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-700">
                Share this direct link with your clients, customers, or add it to your social media bios.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={publicUrl}
                  className="w-full text-xs font-mono bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-800 outline-none select-all"
                />
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleCopy(publicUrl)}
                    className="flex-1 sm:flex-initial px-4 py-2 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-1.5"
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 sm:flex-initial px-4 py-2 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 whitespace-nowrap cursor-pointer transition-colors flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-external-link-line" />
                    Open
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeTab === "iframe" && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-700">Use this iFrame code to embed the form on any website</p>
              <div className="relative">
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre">{iframeCode}</pre>
                </div>
                <button
                  onClick={() => handleCopy(iframeCode)}
                  className="absolute top-3 right-3 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "embed" && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-700">Copy and paste this code into your website HTML</p>
              <div className="relative">
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre">{embedCode}</pre>
                </div>
                <button
                  onClick={() => handleCopy(embedCode)}
                  className="absolute top-3 right-3 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}

          {activeTab === "webhook" && (
            <div className="space-y-4">
              <p className="text-sm text-foreground-700">Submit form inquiries programmatically to this endpoint</p>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 uppercase mb-1">POST Endpoint</label>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                  <code className="text-xs text-foreground-900 font-mono flex-1 break-all">{webhookUrl}</code>
                  <button
                    onClick={() => handleCopy(webhookUrl)}
                    className="text-indigo-600 hover:text-indigo-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-700 uppercase mb-1">JSON Payload Example</label>
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre">{jsonExample}</pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}